import { useRef } from 'react';

import { StudentApi } from 'apis/StudentApi';

/**
 * The student site's question viewer renders teacher previews through a real
 * teacher -> student relationship (WPL3_GetTeacherStudentRelationship validates
 * that the student belongs to a partner within the teacher's license
 * hierarchy), so opening a preview requires the id of SOME active student in
 * the current partner — an empty student id makes the viewer fail with
 * "This question could not be loaded".
 *
 * Resolves one lazily via the paged student lookup (any active student of the
 * partner passes the relationship check) and caches it for the lifetime of the
 * page. Returns 0 when the partner has no active students, so callers can
 * explain the requirement instead of opening a broken viewer.
 */
export const usePreviewStudent = () => {

    const cacheRef = useRef<number | null>(null);

    const resolvePreviewStudentId = async (): Promise<number> => {

        if (cacheRef.current !== null) return cacheRef.current;

        try {

            const page = await StudentApi.GetAllPaged(1, 1);
            const id = Number(page?.Items?.[0]?.Id ?? 0);

            // Cache hits only: a failed/empty lookup stays retryable on the next preview.
            if (id > 0) cacheRef.current = id;

            return id;

        } catch {

            return 0;

        }

    };

    return { resolvePreviewStudentId };

};
